import React, { useCallback, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useDispatch } from "react-redux";

import {
  StyledArtboardBlock,
  StyledAudioVerticalResizeHandler,
} from "./styled";
import { DURATION_PIXEL_RATIO } from "../../_components/styled/variable";
import ArtBoardPreviewStaticCanvas from "../ArtBoardPreviewStaticCanvas";
import {
  StyledAnimationButton,
  StyledArtboardPreviewDuration,
  StyledArtBoardPreviewTitle,
} from "../../_components/styled/details/styleArtBoardEditor";
import ArtBoardPreviewDropdownActions from "../ArtBoardPreviewDropdownActions";
import { ReactComponent as MotionIcon } from "../../assets/icons/motion.svg";
import { TIMELINE_MINIMUM_PAGE_DURATION } from "../../_utils/constants";
import { selectArtboard } from "../../_actions/motionActions";
import { Box, Tooltip } from "@chakra-ui/react";
import { FaGripLinesVertical } from "react-icons/fa";
import { useTranslation } from "react-i18next";

dayjs.extend(duration);

function ArtboardBlock({
  index,
  order,
  isActive,
  page,
  onClick,
  scale,
  parentId,
  onResize,
  onResizeStop,
  offset,
  width,
  onClickAnimate,
  canvasRef
}) {
  const duration = width / (DURATION_PIXEL_RATIO * scale);

  let clickedOnce = useRef(false);
  let timeoutId = 0;
  const { t } = useTranslation(["studio"]);

  const dispatch = useDispatch();

  const selectArtboardAction = useCallback(
    (id = null, idx = null) => dispatch(selectArtboard(id, idx)),
    [dispatch]
  );

  return (
    <StyledArtboardBlock
      disableDragging={true}
      bounds={parentId}
      isactive={isActive.toString()}
      onClick={onClick}
      default={{
        x: offset,
        y: 0,
        width,
      }}
      size={{ width: width, height: "100%" }}
      position={{ x: offset, y: 0 }}
      onResize={onResize}
      onResizeStop={onResizeStop}
      minWidth={DURATION_PIXEL_RATIO * scale * TIMELINE_MINIMUM_PAGE_DURATION}
      enableResizing={{
        top: false,
        bottom: false,
        left: false,
        right: true,
      }}
      resizeHandleStyles={{
        right: {
          right: "12px",
        },
      }}
      resizeHandleComponent={{
        right: (
          <Tooltip placement="top" hasArrow label={t("trim")}>
            <StyledAudioVerticalResizeHandler>
              <FaGripLinesVertical />
            </StyledAudioVerticalResizeHandler>
          </Tooltip>
        ),
      }}
    >
      <div
        className="block"
        onClick={() => {
          if (!clickedOnce.current) {
            clickedOnce.current = true;
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
              clickedOnce.current = false;
            }, 250);
          } else {
            selectArtboardAction(page.id, index);
          }
        }}
      >
        <StyledArtBoardPreviewTitle
          style={{ justifyContent: "start" }}
          location="preview-container"
        >
          <p>{order + 1}</p>
        </StyledArtBoardPreviewTitle>

        <Box w="100%" h="100%" overflow="hidden">
          <ArtBoardPreviewStaticCanvas page={page} canvasRef={canvasRef} />
        </Box>

        <Box position="absolute" top={0} right="8px">
          <ArtBoardPreviewDropdownActions
            pageId={page.id}
            showForActiveArtboardOnly={false}
            showTimelineOptions={true}
          />
        </Box>

        <StyledAnimationButton onClick={onClickAnimate}>
          <MotionIcon />
        </StyledAnimationButton>
        {duration && (
          <StyledArtboardPreviewDuration>
            {dayjs.duration(duration, "seconds").format("mm:ss")}
          </StyledArtboardPreviewDuration>
        )}
      </div>
    </StyledArtboardBlock>
  );
}

ArtboardBlock.defaultProps = {
  scale: 1,
};

ArtboardBlock.propTypes = {
  index: PropTypes.number.isRequired,
  parentId: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  page: PropTypes.object.isRequired,
  offset: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  start: PropTypes.number,
  end: PropTypes.number,
  id: PropTypes.string,
  order: PropTypes.number,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  scale: PropTypes.number,
  onResize: PropTypes.func.isRequired,
  onResizeStop: PropTypes.func.isRequired,
  onClickAnimate: PropTypes.func.isRequired,
};

export default ArtboardBlock;
